/**
 * CategoryPricingAdminPage — one page to manage:
 *   • Auto shipping-size + 3-month launch discount for food/grocery/etc.
 *   • Per-km + base pricing for courier / same-day / logistics vendors.
 *   • Platform-wide priority-delivery multiplier.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Truck, Sparkles, Save, Loader2, RotateCcw, Timer, Zap } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const isLogistics = (entry) =>
  entry && (entry.base_fee_cad !== undefined || entry.per_km_cad !== undefined);

const CategoryPricingAdminPage = () => {
  const [cfg, setCfg] = useState(null);
  const [priorityMult, setPriorityMult] = useState(1.5);
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pricing, shipping] = await Promise.all([
        api.get('/admin/category-pricing').then((r) => r.data),
        api.get('/admin/shipping-sizes').then((r) => r.data),
      ]);
      setCfg(pricing.category_config || {});
      setPriorityMult(pricing.priority_delivery_multiplier || 1.5);
      setSizes(shipping.sizes || []);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateEntry = (key, patch) => {
    setCfg((c) => ({ ...c, [key]: { ...(c[key] || {}), ...patch } }));
    setDirty(true);
  };

  const setDiscount3Months = (key) => {
    const d = new Date();
    d.setMonth(d.getMonth() + 3);
    updateEntry(key, {
      discount_active: true,
      discount_pct: 100,
      discount_expires_at: d.toISOString(),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/category-pricing', {
        category_config: cfg,
        priority_delivery_multiplier: priorityMult,
      });
      setCfg(data.category_config);
      setPriorityMult(data.priority_delivery_multiplier);
      setDirty(false);
      toast.success('Category pricing saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const { retailEntries, logisticsEntries } = useMemo(() => {
    const retail = [];
    const logistics = [];
    Object.entries(cfg || {}).forEach(([k, v]) => {
      (isLogistics(v) ? logistics : retail).push([k, v || {}]);
    });
    retail.sort((a, b) => a[0].localeCompare(b[0]));
    logistics.sort((a, b) => a[0].localeCompare(b[0]));
    return { retailEntries: retail, logisticsEntries: logistics };
  }, [cfg]);

  return (
    <div data-testid="category-pricing-admin">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Truck className="h-6 w-6 text-emerald-600" />
            <h1 className="text-2xl font-semibold text-slate-900">Category pricing</h1>
          </div>
          <p className="text-slate-500 mt-1 text-sm max-w-2xl">
            Set the auto-selected parcel size + launch discount for retail categories, and the
            base + per-km rate for courier / same-day / logistics categories.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="category-pricing-reload">
            <RotateCcw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Reload
          </Button>
          <Button
            onClick={save}
            disabled={saving || !dirty || loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="category-pricing-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save
          </Button>
        </div>
      </div>

      <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="h-5 w-5 text-amber-500" />
          <h2 className="font-semibold text-slate-900">Priority delivery multiplier</h2>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          Applied on top of the delivery fee when the customer ticks &ldquo;Priority&rdquo; at checkout.
        </p>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            step="0.1"
            min="1"
            value={priorityMult}
            onChange={(e) => { setPriorityMult(Number(e.target.value) || 1); setDirty(true); }}
            className="h-10 w-28 text-right font-mono"
            data-testid="priority-multiplier-input"
          />
          <span className="text-sm text-slate-500">× base delivery fee</span>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading category config…
        </div>
      ) : (
        <>
          {/* Retail: auto-size + launch discount */}
          <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="retail-categories">
            <h2 className="font-semibold text-slate-900 mb-1">Retail categories — auto shipping size</h2>
            <p className="text-sm text-slate-500 mb-4">
              Food, groceries etc. automatically use the picked box. If a launch discount is active
              and the expiry hasn&apos;t passed, the extra box cost is waived from the customer&apos;s bill.
            </p>
            <div className="space-y-3">
              {retailEntries.map(([key, entry]) => {
                const expired = entry.discount_expires_at
                  && new Date(entry.discount_expires_at) < new Date();
                return (
                  <div
                    key={key}
                    className="border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-end"
                    data-testid={`retail-row-${key}`}
                  >
                    <div className="md:col-span-2">
                      <div className="text-xs uppercase tracking-wide text-slate-400">Category</div>
                      <div className="font-semibold text-slate-900">
                        {entry.label || key}
                        <span className="ml-2 text-xs text-slate-400 font-mono">{key}</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Auto size</Label>
                      <select
                        value={entry.auto_size_id || ''}
                        onChange={(e) => updateEntry(key, { auto_size_id: e.target.value })}
                        className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm"
                        data-testid={`retail-size-${key}`}
                      >
                        <option value="">— none —</option>
                        {sizes.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name} (×{Number(s.price_multiplier).toFixed(1)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Discount %</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={entry.discount_pct ?? 100}
                        onChange={(e) => updateEntry(key, { discount_pct: Number(e.target.value) })}
                        className="h-9 text-right font-mono mt-1"
                        data-testid={`retail-discount-${key}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Expires</Label>
                      <Input
                        type="date"
                        value={entry.discount_expires_at ? entry.discount_expires_at.slice(0, 10) : ''}
                        onChange={(e) => updateEntry(key, {
                          discount_expires_at: e.target.value ? new Date(e.target.value).toISOString() : null,
                        })}
                        className="h-9 mt-1"
                        data-testid={`retail-expiry-${key}`}
                      />
                      {expired && (
                        <div className="text-[11px] text-rose-500 mt-1">Expired</div>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <Label className="text-xs text-slate-500 block">Active</Label>
                        <Switch
                          checked={!!entry.discount_active}
                          onCheckedChange={(v) => updateEntry(key, { discount_active: v })}
                          data-testid={`retail-active-${key}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setDiscount3Months(key)}
                        className="text-xs text-emerald-700 hover:text-emerald-900 flex items-center gap-1"
                        data-testid={`retail-3mo-${key}`}
                      >
                        <Timer className="h-3.5 w-3.5" /> +3mo
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Logistics: base + per-km */}
          <section className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="logistics-categories">
            <h2 className="font-semibold text-slate-900 mb-1">Logistics categories — special rates</h2>
            <p className="text-sm text-slate-500 mb-4">
              Courier, same-day and business-logistics vendors are billed by distance instead of using
              the default platform fee. Enter the CAD base fee and per-km cost for each.
            </p>
            <div className="space-y-3">
              {logisticsEntries.map(([key, entry]) => (
                <div
                  key={key}
                  className="border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end"
                  data-testid={`logistics-row-${key}`}
                >
                  <div className="md:col-span-2">
                    <div className="text-xs uppercase tracking-wide text-slate-400">Category</div>
                    <div className="font-semibold text-slate-900">
                      {entry.label || key}
                      <span className="ml-2 text-xs text-slate-400 font-mono">{key}</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Base fee (CAD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={entry.base_fee_cad ?? 0}
                      onChange={(e) => updateEntry(key, { base_fee_cad: Number(e.target.value) })}
                      className="h-9 text-right font-mono mt-1"
                      data-testid={`logistics-base-${key}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Per km (CAD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={entry.per_km_cad ?? 0}
                      onChange={(e) => updateEntry(key, { per_km_cad: Number(e.target.value) })}
                      className="h-9 text-right font-mono mt-1"
                      data-testid={`logistics-perkm-${key}`}
                    />
                  </div>
                  <div className="text-xs text-slate-500">
                    <div className="font-mono text-slate-800">
                      = CAD {(Number(entry.base_fee_cad || 0) + Number(entry.per_km_cad || 0) * 5).toFixed(2)}
                    </div>
                    <div className="text-[11px]">for a 5 km trip</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {dirty && (
            <div className="text-xs text-amber-600 flex items-center gap-1.5 mb-3">
              <Sparkles className="h-3 w-3" /> Unsaved changes — click Save.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CategoryPricingAdminPage;
